import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const MAX_PERCENTAGE_FRACTION_DIGITS = 20;

const decimalDigitsPattern = /^\d+([.,]\d+)?$/;

function hasMultipleSeparators(value: string): boolean {
  const dotCount = (value.match(/\./g) ?? []).length;
  const commaCount = (value.match(/,/g) ?? []).length;
  return dotCount + commaCount > 1;
}

function normalize(value: string): string {
  return value.replace(',', '.');
}

function isGreaterThanZero(normalized: string): boolean {
  const digitsOnly = normalized.replace('.', '');
  return /[1-9]/.test(digitsOnly);
}

function isLessOrEqualHundred(normalized: string): boolean {
  const [integerPartRaw, fractionPart = ''] = normalized.split('.');
  const integerPart = integerPartRaw.replace(/^0+(?=\d)/, '');
  const integerValue = BigInt(integerPart.length > 0 ? integerPart : '0');

  if (integerValue < 100n) {
    return true;
  }
  if (integerValue > 100n) {
    return false;
  }
  return /^0*$/.test(fractionPart);
}

export function normalizePercentageForApi(rawValue: string | null | undefined): string | null {
  if (typeof rawValue !== 'string') {
    return null;
  }
  const trimmed = rawValue.trim();
  if (trimmed.length === 0 || hasMultipleSeparators(trimmed)) {
    return null;
  }
  if (!decimalDigitsPattern.test(trimmed)) {
    return null;
  }

  const normalized = normalize(trimmed);
  const [, fractionPart = ''] = normalized.split('.');
  if (fractionPart.length > MAX_PERCENTAGE_FRACTION_DIGITS) {
    return null;
  }
  if (!isGreaterThanZero(normalized)) {
    return null;
  }
  if (!isLessOrEqualHundred(normalized)) {
    return null;
  }
  return normalized;
}

export function percentageInputValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const rawValue = control.value;
    if (rawValue == null || rawValue === '') {
      return null;
    }
    if (typeof rawValue !== 'string') {
      return { invalidDecimal: true };
    }

    const trimmed = rawValue.trim();
    if (trimmed.length === 0) {
      return null;
    }

    if (hasMultipleSeparators(trimmed)) {
      return { invalidDecimal: true };
    }

    if (!decimalDigitsPattern.test(trimmed)) {
      return { invalidDecimal: true };
    }

    const normalized = normalize(trimmed);
    const [, fractionPart = ''] = normalized.split('.');
    if (fractionPart.length > MAX_PERCENTAGE_FRACTION_DIGITS) {
      return { maxFractionDigits: true };
    }
    if (!isGreaterThanZero(normalized)) {
      return { min: true };
    }
    if (!isLessOrEqualHundred(normalized)) {
      return { max: true };
    }

    return null;
  };
}

export function formatPercentageInput(value: number | string | null | undefined): string | null {
  if (value == null) {
    return null;
  }
  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : null;
}
